import { Avatar, Button, Link, Stack, Typography } from '@mui/material';
import { IApplication, getUploadedFile } from 'API/applies/applies';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useImposter, useIsDesktop } from 'Hooks';
import { DateDivider } from './DateDivider';
import { format } from 'date-fns';
import { APPLY_TYPES } from 'Enum/Applies';
import { Download2, LogoSmall, PdfDocument } from 'Images';
import { ChatBubble } from './ChatBubble';
import { IconAndText } from './ChatBubble/IconAndText';
import { isObjectNotEmpty } from 'Utils/isObjectEmpty';
import { shortDateFormat } from 'Config';
import { downloadFile, getShortText } from 'Utils';
import { useLoader } from 'Hooks/useLoader';
import { requestFile } from 'API/medicalReferrals';
import { LinkArrowLeft2 } from 'Components';
import { Fragment, useCallback } from 'react';

interface IDetailsBody {
  application: IApplication;
}

export const DetailsBody = ({ application }: IDetailsBody) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Applies);
  const isDesktop = useIsDesktop();
  const scaleLoader = isDesktop ? 'scale(0.5)' : 'scale(0.25)';
  const { imposterId, authenticatedUser, fakeUser } = useImposter();
  const { setIsLoading } = useLoader(scaleLoader, undefined, undefined, false);

  const getText = useCallback(() => {
    if (application.userNote) return application.userNote;
    return application.type === APPLY_TYPES.telephone
      ? `${dictionary.phoneRequestOpened} ${application.subject}`
      : `${dictionary.webRequestOpened} ${application.subject}`;
  }, [application.userNote, application.type, application.subject, dictionary]);

  const download = useCallback(
    async (
      e: React.MouseEvent<HTMLElement, MouseEvent>,
      medicalReferralId: number
    ) => {
      setIsLoading(true);
      e.stopPropagation();
      const file = medicalReferralId
        ? await requestFile(medicalReferralId)
        : null;
      setIsLoading(false);
      if (file?.fileContentBase64) {
        downloadFile(
          file.fileContentBase64,
          'pdf',
          file.documentName || 'New Document'
        );
      }
    },
    [setIsLoading]
  );

  return (
    <Stack gap='32px' padding={'32px 0 40px 0'}>
      {application.medicalReferrals &&
        application.medicalReferrals?.length > 0 && (
          <>
            {application.statusDate && (
              <DateDivider
                text={`${dictionary.updateDate} ${format(
                  new Date(application.statusDate),
                  shortDateFormat
                )}`}
              />
            )}
            {application.status.status === 6 && (
              <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
                <IconAndText
                  isUsersText={false}
                  title={dictionary.rehabilitationDepartment}
                  text={dictionary.partialHandling}
                />
              </ChatBubble>
            )}

            <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
              <IconAndText
                isUsersText={false}
                title={dictionary.rehabilitationDepartment}
                text={dictionary.yourMedicalRefferal}
              />
              {application.medicalReferrals.map((item, index) => (
                <Stack
                  key={index}
                  flexDirection={'row'}
                  mb={1}
                  alignItems={'center'}
                  sx={{ cursor: 'pointer', pl: 5 }}
                  component={'a'}
                  tabIndex={0}
                  onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                    download(e, item.id)
                  }
                >
                  <Avatar
                    src={Download2}
                    alt={''}
                    sx={{
                      width: '20px',
                      height: '20px',
                      mr: 1,
                    }}
                  />
                  <Typography variant='body5' color='primary.main'>
                    {item.catalogDesc || dictionary.downloadYourFile}
                  </Typography>
                </Stack>
              ))}
            </ChatBubble>

            <ChatBubble isRtl={false} sxProps={{ mt: '-20px' }}>
              <LinkArrowLeft2
                href='/medical-referrals'
                text={dictionary.allMedicalRefferal}
                newWindow={false}
                sx={{ p: '15px 32px 15px 24px' }}
              />
            </ChatBubble>
          </>
        )}
      {application.benefits && application.benefits?.length > 0 && (
        <>
          {application.statusDate && (
            <DateDivider
              text={`${dictionary.updateDate} ${format(
                new Date(application.statusDate),
                shortDateFormat
              )}`}
            />
          )}
          {application.benefits.map((item, index) => (
            <Fragment key={index}>
              <ChatBubble minWidth={isDesktop ? '345px' : 'auto'} isRtl={false}>
                <IconAndText
                  isUsersText={false}
                  title={dictionary.rehabilitationDepartment}
                  text={dictionary.benefitsText(item.part, item.appMaanakSum)}
                  amount={
                    item.appMaanakSum
                      ? `${item.appMaanakSum} ${dictionary.nis}`
                      : undefined
                  }
                />
              </ChatBubble>
              <ChatBubble isRtl={false} sxProps={{ mt: '-20px' }}>
                <LinkArrowLeft2
                  href='/payments&deductions'
                  text={dictionary.moreInfoInPaymentsPage}
                  newWindow={false}
                  sx={{ p: '15px 32px 15px 24px' }}
                />
              </ChatBubble>
            </Fragment>
          ))}
        </>
      )}
      {application.link && isObjectNotEmpty(application.link) && (
        <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
          <Stack gap='6px' maxWidth='320px' p='16px'>
            <Typography
              variant={'body4'}
              fontSize={isDesktop ? '18px' : undefined}
            >
              {application.link.linkPrefixText}
            </Typography>
            <Stack direction={'row'} gap={'4px'} flexWrap={'wrap'}>
              <Link
                fontSize={isDesktop ? '18px' : undefined}
                href={application.link.linkUrl}
              >
                <Typography variant={'body4'}>
                  {application.link.linkText}
                </Typography>
              </Link>
              <Typography variant={'body3'}>
                {application.link.linkSuffixText}
              </Typography>
            </Stack>
          </Stack>
        </ChatBubble>
      )}
      {application.replyDate && (
        <>
          <DateDivider
            text={`${dictionary.updateDate} ${format(
              new Date(application.replyDate),
              shortDateFormat
            )}`}
          />
          <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
            <Stack gap='8px' direction='row' p='16px 16px 16px 8px'>
              <Avatar
                src={LogoSmall}
                alt=''
                sx={{ width: '28px', height: '28px' }}
              />
              <Stack pt='3px' gap='9px'>
                <Typography
                  fontSize={isDesktop ? '16px' : '14px'}
                  lineHeight={isDesktop ? '22px' : '20px'}
                  fontWeight='700'
                >
                  {dictionary.rehabilitationDepartment}
                </Typography>
                <Typography
                  fontSize={isDesktop ? '18px' : '16px'}
                  lineHeight={isDesktop ? '24px' : '22px'}
                >
                  {dictionary.replyDetails}
                </Typography>
                <Typography
                  fontSize={isDesktop ? '18px' : '16px'}
                  lineHeight={isDesktop ? '24px' : '22px'}
                  variant='body3'
                >
                  {application.reply.trim() || dictionary.noReplyDetails}
                </Typography>
              </Stack>
            </Stack>
          </ChatBubble>
        </>
      )}
      {application.forwardedForProcessing && (
        <>
          <DateDivider
            text={`${dictionary.updateDate} ${format(
              new Date(application.forwardedForProcessing),
              shortDateFormat
            )}`}
          />
          <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
            <IconAndText
              isUsersText={false}
              title={dictionary.rehabilitationDepartment}
              text={dictionary.yourRequestWasTransferred}
            />
          </ChatBubble>
        </>
      )}
      {application.shemeshCreateDate && (
        <>
          <DateDivider
            text={`${dictionary.updateDate} ${format(
              new Date(application.shemeshCreateDate),
              shortDateFormat
            )}`}
          />
          <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
            <IconAndText
              isUsersText={false}
              title={dictionary.rehabilitationDepartment}
              text={dictionary.applyOpened(application.subject)}
            />
          </ChatBubble>
        </>
      )}
      {(application.createDate || application.shemeshCreateDate) && (
        <>
          <DateDivider
            text={`${dictionary.applyOpenDate} ${format(
              new Date(application.createDate || application.shemeshCreateDate),
              shortDateFormat
            )}`}
          />
          <ChatBubble
            minWidth={isDesktop ? '300px' : 'auto'}
            maxWidth='458px'
            isRtl={true}
          >
            <IconAndText
              isUsersText={true}
              title={
                imposterId
                  ? `${fakeUser.firstName} ${fakeUser.lastName}`
                  : `${authenticatedUser.firstName} ${authenticatedUser.lastName}`
              }
              text={getText()}
            />
            {application?.files?.length > 0 && (
              <Stack
                flexDirection='row'
                flexWrap={'wrap'}
                width={'auto'}
                m='0 24px 16px 42px'
                gap='24px'
              >
                {application.files.map((file) => (
                  <Button
                    key={file.id}
                    sx={{
                      width: '200px',
                      height: '42px',
                      gap: '5px',
                      bgcolor: '#DBE7FA',
                      borderRadius: '8px',
                      p: '7px 12px 9px',
                    }}
                    onClick={async () => {
                      const fileData = await getUploadedFile(file.id);

                      if (fileData?.fileContentBase64) {
                        downloadFile(
                          fileData.fileContentBase64,
                          file.userGivenName.split('.').pop()!,
                          file.userGivenName
                        );
                      }
                    }}
                  >
                    <Avatar
                      sx={{ width: '14px', height: '22px' }}
                      src={PdfDocument}
                      alt={dictionary.documentToDownload}
                    />
                    <Typography
                      variant={'body2'}
                      title={file.userGivenName}
                      fontSize='18px'
                    >
                      {getShortText(file.userGivenName, 16)}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            )}
          </ChatBubble>
        </>
      )}
    </Stack>
  );
};
